import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Popup, DatetimePicker } from 'react-vant'
import { APIWeb } from '@/api'
import RefreshList from '@/components/RefreshList'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import Ripple from '@/components/Ripple'
import SkeletonBox from '@/components/SkeletonBox'
import EmptyState from '@/components/EmptyState'
import EventIcon from '@/components/EventIcon'
import { useAllRecordListType } from '@/hooks/listStatus'
import { formatThousands, formatDateTime } from '@/utils'
import dayjs from 'dayjs'

// 状态筛选项
type StatusFilter = 'all' | 'completed' | 'pending' | 'failed'

// 所有事件类型枚举值
const ALL_EVENT_TYPES = [
  'member_transfer_out',
  'member_transfer_in',
  'open_virtual_card_fee',
  'open_physical_card_fee',
  'card_auth',
  'card_auth_reverse',
  'card_clearing',
  'card_clearing_refund',
  'bank_transfer',
  'token_recharge',
  'exchange',
  'claim',
  'auth_fee_refund',
  'card_recharge',
  'card_payout',
  'card_settlement',
  'card_close_refund',
  'authentication_approved',
] as const

// 获取状态颜色和文字
const getStatusInfo = (status: string, t: (key: string) => string) => {
  switch (status) {
    case 'pending':
      return { text: t('进行中'), color: '#F97316' }
    case 'failed':
      return { text: t('失败'), color: '#EF4444' }
    case 'completed':
    default:
      return { text: t('已完成'), color: '#86909C' }
  }
}

export default React.memo(function AllRecords() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { getAllRecordListType } = useAllRecordListType()

  // 数据状态
  const [data, setData] = useState<AssetFlowTableListItem[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  // 筛选状态
  const [selectedEvent, setSelectedEvent] = useState<string>('all')
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>('completed')

  // 弹窗状态
  const [showTypePopup, setShowTypePopup] = useState(false)
  const [showStatusPopup, setShowStatusPopup] = useState(false)
  const [showDatePopup, setShowDatePopup] = useState(false)

  // 日期筛选状态
  type DateQuickFilter = '7d' | '1m' | '3m' | null
  const [dateQuickFilter, setDateQuickFilter] = useState<DateQuickFilter>(null)
  const [dateStart, setDateStart] = useState<Date | null>(null)
  const [dateEnd, setDateEnd] = useState<Date | null>(null)
  // 弹窗内临时状态（点确认才生效）
  const [tempQuickFilter, setTempQuickFilter] = useState<DateQuickFilter>(null)
  const [tempDateStart, setTempDateStart] = useState<Date | null>(null)
  const [tempDateEnd, setTempDateEnd] = useState<Date | null>(null)
  // 当前正在编辑的日期（start / end）
  const [editingDate, setEditingDate] = useState<'start' | 'end'>('start')

  // 交易类型选项（基于 useAllRecordListType 枚举）
  const typeOptions = useMemo(() => [
    { label: t('全部'), value: 'all' },
    ...ALL_EVENT_TYPES.map(event => ({
      label: getAllRecordListType(event, event),
      value: event,
    })),
  ], [t, getAllRecordListType])

  // 状态选项
  const statusOptions: { label: string; value: StatusFilter }[] = useMemo(() => [
    { label: t('已完成'), value: 'completed' },
  ], [t])

  // 获取当前选中类型的显示文字
  const selectedTypeLabel = useMemo(() => {
    return typeOptions.find(o => o.value === selectedEvent)?.label || t('全部')
  }, [selectedEvent, typeOptions, t])

  // 获取当前选中状态的显示文字
  const selectedStatusLabel = useMemo(() => {
    return statusOptions.find(o => o.value === selectedStatus)?.label || t('状态')
  }, [selectedStatus, statusOptions, t])

  // 构建请求参数
  const buildRequestParams = useCallback((page: number): AssetFlowTableListRequest => {
    const params: AssetFlowTableListRequest = {
      page,
      page_size: pageSize,
    }
    // 事件类型（服务端筛选）
    if (selectedEvent !== 'all') {
      params.event = selectedEvent
    }
    // 日期范围（服务端筛选）
    if (dateStart) {
      params.start_day = dayjs(dateStart).format('YYYY-MM-DD')
    }
    if (dateEnd) {
      params.end_day = dayjs(dateEnd).format('YYYY-MM-DD')
    }
    return params
  }, [selectedEvent, dateStart, dateEnd])

  // 使用 ref 做请求锁，避免 setState 异步导致重复请求（进入页面、加载更多各触发两次）
  const loadingRef = useRef(false)

  // 加载数据
  const fetchData = useCallback(async (page: number, isAppend: boolean = false) => {
    if (loadingRef.current) return
    loadingRef.current = true
    setLoading(true)
    try {
      const params = buildRequestParams(page)
      const result = await APIWeb.getAssetFlowTableList(params)

      if (result?.list) {
        const newData = result.list
        if (isAppend) {
          setData(prev => [...prev, ...newData])
        } else {
          setData(newData)
        }
        if (newData.length < pageSize) {
          setHasMore(false)
        } else {
          setHasMore(true)
          if (!isAppend) {
            setCurrentPage(2)
          } else {
            setCurrentPage(prev => prev + 1)
          }
        }
      } else {
        if (!isAppend) {
          setData([])
        }
        setHasMore(false)
      }
    } catch (error) {
      console.error('获取交易记录失败:', error)
      setHasMore(false)
    } finally {
      loadingRef.current = false
      setLoading(false)
    }
  }, [buildRequestParams])

  // 初始加载 + 筛选条件变化时重新加载（合并为一个 effect，避免重复请求）
  useEffect(() => {
    setData([])
    setCurrentPage(1)
    setHasMore(true)
    fetchData(1, false)
  }, [selectedEvent, dateStart, dateEnd])

  // 加载更多
  const loadMore = useCallback(async () => {
    await fetchData(currentPage, true)
  }, [fetchData, currentPage])

  // 下拉刷新
  const onRefresh = useCallback(async () => {
    setCurrentPage(1)
    setHasMore(true)
    await fetchData(1, false)
  }, [fetchData])

  // 筛选后的数据（交易类型和日期均为服务端筛选）
  const filteredData = useMemo(() => {
    let result = data

    // 按状态筛选 (当前API无状态字段，默认全部为已完成)
    if (selectedStatus !== 'all') {
      if (selectedStatus !== 'completed') {
        result = []
      }
    }

    return result
  }, [data, selectedStatus])

  // 点击记录跳转详情
  const handleRecordClick = useCallback((record: AssetFlowTableListItem) => {
    navigate('/record-detail', { state: { record } })
  }, [navigate])

  // 选择交易类型
  const handleSelectType = useCallback((eventType: string) => {
    setSelectedEvent(eventType)
    setShowTypePopup(false)
  }, [])

  // 选择状态
  const handleSelectStatus = useCallback((status: StatusFilter) => {
    setSelectedStatus(status)
    setShowStatusPopup(false)
  }, [])

  // 骨架屏
  const renderSkeleton = useCallback(() => (
    <div className="flex items-center py-4 px-5">
      <SkeletonBox className="w-11 h-11 rounded-full flex-shrink-0" />
      <div className="flex-1 ml-3">
        <SkeletonBox className="w-20 h-4 rounded mb-2" />
        <SkeletonBox className="w-36 h-3 rounded" />
      </div>
      <div className="text-right">
        <SkeletonBox className="w-20 h-4 rounded mb-2" />
        <SkeletonBox className="w-12 h-3 rounded ml-auto" />
      </div>
    </div>
  ), [])

  // 渲染单条记录
  const renderItem = useCallback((item: AssetFlowTableListItem) => {
    const statusInfo = getStatusInfo('completed', t) // 当前默认已完成
    const changeNum = parseFloat(item.change) || 0
    const isPositive = changeNum >= 0
    const currencyName = item.type?.toUpperCase() || 'USD'
    const isFiat = ['USD'].includes(currencyName)

    return (
      <Ripple className="rounded-xl">
        <div
          className="flex items-center py-4 px-5"
          onClick={() => handleRecordClick(item)}
        >
          {/* 左侧图标 */}
          <EventIcon event={item.event} />

          {/* 中间内容 */}
          <div className="flex-1 ml-3 min-w-0">
            <div className="text-sm font-medium text-[#1D2129] truncate">
              {getAllRecordListType(item.event || '', item.event || '')}
            </div>
            <div className="text-xs text-[#86909C] mt-1">
              {formatDateTime(item.updated_at, 'YYYY/MM/DD HH:mm')}
            </div>
          </div>

          {/* 右侧金额和状态 */}
          <div className="text-right flex-shrink-0 ml-3">
            <div className="text-sm font-semibold text-[#1D2129]">
              {isPositive ? '+' : ''}{isFiat ? '$' : ''}{formatThousands(changeNum, 2, true)}{!isFiat ? ` ${currencyName}` : ''}
            </div>
            <div className="text-xs mt-1" style={{ color: statusInfo.color }}>
              {statusInfo.text}
            </div>
          </div>
        </div>
      </Ripple>
    )
  }, [getAllRecordListType, handleRecordClick, t])

  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium text-black">{t('交易记录')}</span>}
        rightChildren={
          <div className="flex items-center justify-center cursor-pointer" onClick={() => navigate('/my/help')}>
            <img src="/icon/question.svg" alt="" className="w-5 h-5" />
          </div>
        }
      />

      {/* 筛选栏 */}
      <div className="flex items-center px-5 pt-3 bg-white border-b border-[#EAECF0] border-b-solid">
        {/* 交易类型筛选 */}
        <Ripple className="rounded-lg">
          <div
            className="flex items-center px-3 py-1.5 text-sm text-[#1D2129] cursor-pointer"
            onClick={() => setShowTypePopup(true)}
          >
            <span className="text-3.5 text-[#1D2129]">
              {selectedTypeLabel}
            </span>
            <img src="/icon/filter.svg" alt="" className="w-3 h-3 ml-1.5" />
          </div>
        </Ripple>

        {/* 日期筛选 */}
        <Ripple className="rounded-lg">
          <div
            className="flex items-center px-3 py-1.5 text-sm text-[#1D2129] cursor-pointer"
            onClick={() => setShowDatePopup(true)}
          >
            <span className="text-3.5 text-[#1D2129]">
              {dateStart && dateEnd
                ? `${dayjs(dateStart).format('YYYY/MM/DD')}-${dayjs(dateEnd).format('YYYY/MM/DD')}`
                : dateStart
                  ? `${dayjs(dateStart).format('YYYY/MM/DD')}~`
                  : dateEnd
                    ? `~${dayjs(dateEnd).format('YYYY/MM/DD')}`
                    : t('日期')
              }
            </span>
            <img src="/icon/filter.svg" alt="" className="w-3 h-3 ml-1.5" />
          </div>
        </Ripple>

        {/* 状态筛选 */}
        {/* <Ripple className="rounded-lg">
          <div
            className="flex items-center px-3 py-1.5 text-sm text-[#1D2129] cursor-pointer"
            onClick={() => setShowStatusPopup(true)}
          >
            <span className="text-3.5 text-[#1D2129]">
              {selectedStatusLabel}
            </span>
            <img src="/icon/filter.svg" alt="" className="w-3 h-3 ml-1.5" />
          </div>
        </Ripple> */}
      </div>

      {/* 交易记录列表 */}
      <div className="bg-white flex flex-col flex-1">
        <RefreshList
          data={filteredData}
          renderItem={renderItem}
          keyExtractor={(item, index) => `${item.event}-${item.updated_at}-${index}`}
          loading={loading}
          hasMore={hasMore}
          onRefresh={onRefresh}
          onLoadMore={loadMore}
          disableAutoLoad
          renderSkeleton={renderSkeleton}
          skeletonCount={8}
          emptyComponent={
            <EmptyState text={t('暂无记录')} />
          }
          finishedText={t('已经全部加载')}
          loadingText={t('加载中')}
          className="flex-1"
        />
      </div>

      {/* 交易类型选择弹窗 */}
      <Popup
        visible={showTypePopup}
        onClose={() => setShowTypePopup(false)}
        position="bottom"
        round
      >
        <div className="px-5 pt-6 pb-8">
          {/* 标题栏 */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-lg font-semibold text-[#1D2129]">{t('选择交易类型')}</span>
            <div className="w-8 h-8 flex items-center justify-center cursor-pointer" onClick={() => setShowTypePopup(false)}>
              <img src="/icon/close.svg" alt="" className="w-5 h-5" />
            </div>
          </div>

          {/* 选项列表 */}
          <div className="space-y-2 max-h-[60vh] overflow-y-auto scrollbar-hide">
            {typeOptions.map(option => (
              <Ripple key={option.value} className="rounded-xl">
                <div
                  className="flex items-center justify-between py-4 px-1 cursor-pointer"
                  onClick={() => handleSelectType(option.value)}
                >
                  <span className="text-base text-[#1D2129]">{option.label}</span>
                  {selectedEvent === option.value && (
                    <div className="w-6 h-6 rounded-full bg-[var(--primary-color)] flex items-center justify-center">
                       <img src="/icon/success-white.svg" alt="" className="w-3 h-2" />
                    </div>
                  )}
                </div>
              </Ripple>
            ))}
          </div>
        </div>
      </Popup>

      {/* 状态选择弹窗 */}
      <Popup
        visible={showStatusPopup}
        onClose={() => setShowStatusPopup(false)}
        position="bottom"
        round
      >
        <div className="px-5 pt-6 pb-8">
          {/* 标题栏 */}
          <div className="flex items-center justify-between mb-6">
            <span className="text-lg font-semibold text-[#1D2129]">{t('状态')}</span>
            <div className="w-8 h-8 flex items-center justify-center cursor-pointer" onClick={() => setShowStatusPopup(false)}>
              <img src="/icon/close.svg" alt="" className="w-5 h-5" />
            </div>
          </div>

          {/* 选项列表 */}
          <div className="space-y-2">
            {statusOptions.map(option => (
              <Ripple key={option.value} className="rounded-xl">
                <div
                  className="flex items-center justify-between py-4 px-1 cursor-pointer"
                  onClick={() => handleSelectStatus(option.value)}
                >
                  <span className="text-base text-[#1D2129]">{option.label}</span>
                  {selectedStatus === option.value && (
                    <div className="w-6 h-6 rounded-full bg-[var(--primary-color)] flex items-center justify-center">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 7L6 10L11 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                  )}
                </div>
              </Ripple>
            ))}
          </div>
        </div>
      </Popup>

      {/* 日期选择弹窗 */}
      <Popup
        visible={showDatePopup}
        onClose={() => setShowDatePopup(false)}
        position="bottom"
        round
        onOpen={() => {
          // 打开弹窗时，将已生效的筛选同步到临时状态
          setTempQuickFilter(dateQuickFilter)
          setTempDateStart(dateStart)
          setTempDateEnd(dateEnd)
          setEditingDate('start')
        }}
      >
        <div className="px-5 pt-6 pb-6">
          {/* 标题栏 */}
          <div className="flex items-center justify-between mb-5">
            <span className="text-lg font-semibold text-[#1D2129]">{t('选择日期')}</span>
            <div className="w-8 h-8 flex items-center justify-center cursor-pointer" onClick={() => setShowDatePopup(false)}>
              <img src="/icon/close.svg" alt="" className="w-5 h-5" />
            </div>
          </div>

          {/* 快捷选项 */}
          <div className="flex space-x-3 mb-5">
            {([
              { label: t('7日'), value: '7d' as DateQuickFilter },
              { label: t('最近1月'), value: '1m' as DateQuickFilter },
              { label: t('最近3月'), value: '3m' as DateQuickFilter },
            ]).map(item => {
              const isActive = tempQuickFilter === item.value
              return (
                <div
                  key={item.value}
                  className={`flex-1 py-2.5 rounded-lg text-center text-sm cursor-pointer border border-solid transition-colors ${isActive
                      ? 'border-[#1D2129] text-[#1D2129] font-medium'
                      : 'border-[#E5E6EB] text-[#86909C]'
                    }`}
                  onClick={() => {
                    const now = new Date()
                    let start: Date
                    if (item.value === '7d') {
                      start = dayjs().subtract(7, 'day').toDate()
                    } else if (item.value === '1m') {
                      start = dayjs().subtract(1, 'month').toDate()
                    } else {
                      start = dayjs().subtract(3, 'month').toDate()
                    }
                    setTempQuickFilter(item.value)
                    setTempDateStart(start)
                    setTempDateEnd(now)
                  }}
                >
                  {item.label}
                </div>
              )
            })}
          </div>

          {/* 日期范围显示 */}
          <div className="flex items-center justify-between mb-4">
            <div
              className={`flex-1 py-2.5 rounded-lg text-center text-sm cursor-pointer border border-solid ${editingDate === 'start' ? 'border-[#1D2129] text-[#1D2129] font-medium' : 'border-[#E5E6EB] text-[#86909C]'
                }`}
              onClick={() => setEditingDate('start')}
            >
              {tempDateStart ? dayjs(tempDateStart).format('YYYY-MM-DD') : t('开始日期')}
            </div>
            <span className="mx-3 text-[#86909C]">—</span>
            <div
              className={`flex-1 py-2.5 rounded-lg text-center text-sm cursor-pointer border border-solid ${editingDate === 'end' ? 'border-[#1D2129] text-[#1D2129] font-medium' : 'border-[#E5E6EB] text-[#86909C]'
                }`}
              onClick={() => setEditingDate('end')}
            >
              {tempDateEnd ? dayjs(tempDateEnd).format('YYYY-MM-DD') : t('结束日期')}
            </div>
          </div>

          {/* DatetimePicker 滚轮选择器 */}
          <DatetimePicker
            type="date"
            showToolbar={false}
            minDate={new Date(2018, 0, 1)}
            maxDate={new Date()}
            value={editingDate === 'start' ? (tempDateStart || new Date()) : (tempDateEnd || new Date())}
            formatter={(type: string, val: string) => {
              if (type === 'year') return `${val}${t('年')}`
              if (type === 'month') return `${val}${t('月')}`
              if (type === 'day') return `${val}${t('日')}`
              return val
            }}
            onChange={(value: Date) => {
              setTempQuickFilter(null)
              if (editingDate === 'start') {
                setTempDateStart(value)
              } else {
                setTempDateEnd(value)
              }
            }}
          />

          {/* 底部按钮 */}
          <div className="flex space-x-3 mt-4">
            <div
              className="flex-1 py-3 rounded-full border border-solid border-[#E5E6EB] text-center text-sm font-medium text-[#1D2129] cursor-pointer"
              onClick={() => {
                setTempQuickFilter(null)
                setTempDateStart(null)
                setTempDateEnd(null)
                setDateQuickFilter(null)
                setDateStart(null)
                setDateEnd(null)
                setShowDatePopup(false)
              }}
            >
              {t('重置')}
            </div>
            <div
              className="flex-1 py-3 rounded-full bg-[var(--primary-color)] text-center text-sm font-medium text-white cursor-pointer"
              onClick={() => {
                setDateQuickFilter(tempQuickFilter)
                setDateStart(tempDateStart)
                setDateEnd(tempDateEnd)
                setShowDatePopup(false)
              }}
            >
              {t('确认')}
            </div>
          </div>
        </div>
      </Popup>
    </PageLayout>
  )
})

